import { walletApiSlice } from '../apiSlice';

export interface ICreateWalletRequest {
	tenantId: string;
	userId: string;
	email: string;
	currency?: string;
}

interface ICreateWalletResponse {
	/** The wire field is `id`, not `walletId` — verified against the live response and
	 *  the OpenAPI schema (`CreateWalletResult.required` leads with "id"). */
	id: string;
	tenantId: string;
	userId: string;
	email: string;
	currency: 'NGN';
	currentBalance: number;
	availableBalance: number;
	pendingBalance: number;
	createdAt: string;
}

export interface IWalletResponse {
	/** The wire field is `id`, not `walletId` (`GetWalletBalanceResult.required` leads with
	 *  "id"). Nothing reads it — `walletId` is resolved from the api.core user lookup — but
	 *  the wrong name was a trap armed for the next reader. */
	id: string;
	userId: string;
	tenantId: string;
	email: string;
	currentBalance: number;
	availableBalance: number;
	pendingBalance: number;
	currency: string;
	creditBuckets: {
		/** Also `id` on the wire, not `bucketId`. This one WAS read: the header popover
		 *  keyed its bucket rows on it and rendered every row with key={undefined}. */
		id: string;
		creditType: string;
		currentBalance: number;
		availableBalance: number;
		pendingBalance: number;
		expiresAt: string | null;
	}[];
	calculatedBalanceFromTransactions: number;
	balanceVerificationPassed: boolean;
}

export interface IWalletTransactionHistoryRequest {
	walletId: string;
	pageNumber?: number;
	pageSize?: number;
	includeInternalTransactions?: boolean;
	toDate?: string;
	fromDate?: string;
	serviceType?: string;
	filterBySource?: number[];
	filterByType?: number[];
}

export interface IWalletTransaction {
	id: string;
	walletId: string;
	bucketId: string | null;
	transactionType: number;
	creditType: string;
	amount: number;
	balanceBefore: number;
	balanceAfter: number;
	reference: string;
	serviceType: string;
	source: number;
	createdAt: string;
	/**
	 * The AppUser.UserId (GUID) of whoever did it. The entity records it, but wallet-service's
	 * transaction list (`WalletTransactionItem`) does not expose it yet — so it is optional, and an
	 * absent value means "unknown", never "the owner".
	 */
	performedByUserId?: string | null;
}
export interface IWalletTransactionHistoryResponse {
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	pageNumber: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
	items: IWalletTransaction[];
}

export const userWalletsApiSlice = walletApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		createWallet: builder.mutation<ICreateWalletResponse, ICreateWalletRequest>({
			query: (body) => ({
				url: '/wallets',
				method: 'POST',
				body,
			}),
		}),
		getWalletDetails: builder.query<IWalletResponse, { walletId?: string; userId?: string }>({
			query: ({ walletId, userId }) => ({
				url: '/wallets/balance',
				params: {
					walletId,
					userId,
				},
			}),
			providesTags: ['WalletBalance'],
		}),
		getWalletTransactionHistory: builder.mutation<IWalletTransactionHistoryResponse, IWalletTransactionHistoryRequest>({
			query: (body) => ({
				url: '/wallets/transactions',
				method: 'POST',
				body,
			}),
		}),
	}),
});

export const {
	useCreateWalletMutation,
	useGetWalletDetailsQuery,
	useLazyGetWalletDetailsQuery,
	useGetWalletTransactionHistoryMutation,
} = userWalletsApiSlice;
